import { BusinessUnit } from "../interfaces/BusinessUnit";
import { ProductData, ProductLaborCrew, RegionalLaborCrew } from "../interfaces/Product";
import { Region } from "../interfaces/Region";
import { User } from "../interfaces/User";
import { fetchData, fetchDataStrict } from "../clients/api";
import { handleError } from "../errors";
import { Result } from "./types";
import { CacheHandle, defineCachedLookup, defineCachedResourceById } from "./helpers";

const fetchProducts = async (params?: { populate?: string[]; regionId?: string }): Promise<Result<ProductData[]>> => {
  try {
    const apiParams: Record<string, string | number | string[]> = {
      populate: params?.populate || [],
    };

    const response = await fetchData({ table: "products", params: apiParams });
    const products = response.data || [];
    return { success: true, data: products };
  } catch (error) {
    const err = handleError(error, { table: "products", params, context: "fetchProducts" });
    return { success: false, error: err };
  }
};

/** Each UPC product's crew in a region, with that crew's blended rates for the region. */
const fetchProductLaborCrews = async (regionId: string): Promise<Result<ProductLaborCrew[]>> => {
  try {
    const response = await fetchDataStrict({ table: "product_labor_crews", params: { region_id: regionId } });
    return { success: true, data: response.data || [] };
  } catch (error) {
    const err = handleError(error, { table: "product_labor_crews", regionId, context: "fetchProductLaborCrews" });
    return { success: false, error: err };
  }
};

/** The UPC crews, each with its makeup and blended rates in a region. */
const fetchLaborCrewsForRegion = async (regionId: string): Promise<Result<RegionalLaborCrew[]>> => {
  try {
    const response = await fetchDataStrict({ table: "labor_crews", params: { region_id: regionId } });
    return { success: true, data: response.data?.crews || [] };
  } catch (error) {
    const err = handleError(error, { table: "labor_crews", regionId, context: "fetchLaborCrewsForRegion" });
    return { success: false, error: err };
  }
};

const fetchRegions = async (): Promise<Result<Region[]>> => {
  try {
    const response = await fetchData({ table: "regions" });
    const regions = response.data || [];
    return { success: true, data: regions };
  } catch (error) {
    const err = handleError(error, { table: "regions", context: "fetchRegions" });
    return { success: false, error: err };
  }
};

const fetchUsers = async (params?: { search?: string }): Promise<Result<User[]>> => {
  try {
    const response = await fetchData({ table: "users", params });
    const users = response.data || [];
    return { success: true, data: users };
  } catch (error) {
    const err = handleError(error, { table: "users", context: "fetchUsers" });
    return { success: false, error: err };
  }
};

const fetchEmployeesUncached = async (): Promise<Result<Array<Record<string, any>>>> => {
  try {
    const response = await fetchData({ table: "employees" });
    const employees = response.data || [];
    return { success: true, data: employees };
  } catch (error) {
    const err = handleError(error, { table: "employees", context: "fetchEmployees" });
    return { success: false, error: err };
  }
};

const fetchPositionsUncached = async (): Promise<Result<Array<Record<string, any>>>> => {
  try {
    const response = await fetchData({ table: "positions" });
    const positions = response.data || [];
    return { success: true, data: positions };
  } catch (error) {
    const err = handleError(error, { table: "positions", context: "fetchPositions" });
    return { success: false, error: err };
  }
};

const fetchBusinessUnits = async (): Promise<Result<BusinessUnit[]>> => {
  try {
    const response = await fetchData({ table: "business_units" });
    const businessUnits = response.data || [];
    return { success: true, data: businessUnits };
  } catch (error) {
    const err = handleError(error, { table: "business_units", context: "fetchBusinessUnits" });
    return { success: false, error: err };
  }
};

const usersByIds = defineCachedResourceById((userIdList: string) =>
  fetchUsers({ search: `users.id;IN;${userIdList}` })
);
const employees = defineCachedLookup(fetchEmployeesUncached);
const positions = defineCachedLookup(fetchPositionsUncached);

const fetchUsersByIds = (userIds: string[]): Promise<Result<User[]>> => usersByIds.get([...userIds].sort().join(","));
const fetchEmployees = employees.get;
const fetchPositions = positions.get;

export const userCaches: CacheHandle[] = [usersByIds];

export const employeeCaches: CacheHandle[] = [employees, positions];

export {
  fetchProducts,
  fetchProductLaborCrews,
  fetchLaborCrewsForRegion,
  fetchRegions,
  fetchUsers,
  fetchUsersByIds,
  fetchEmployees,
  fetchPositions,
  fetchBusinessUnits,
};
